import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, doc, setDoc, deleteDoc, addDoc } from 'firebase/firestore';
import './AdminPage.css';

const AdminHSBakbord = () => {
  const [records1bak, setRecords1bak] = useState([]);
  const [records3bak, setRecords3bak] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
  
  // Form state for editing records
  const [editingType, setEditingType] = useState(null); // '1bak' or '3bak'
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    position: '',
    time: '',
    name: ''
  });

  // Fetch bak records from Firestore
  useEffect(() => {
    const fetchBakRecords = async () => {
      try {
        setLoading(true);
        
        // Fetch 1BAK records
        const records1bakCollection = collection(db, 'records1bak');
        const records1bakSnapshot = await getDocs(records1bakCollection);
        
        // Fetch 3BAK records
        const records3bakCollection = collection(db, 'records3bak');
        const records3bakSnapshot = await getDocs(records3bakCollection);
        
        if (records1bakSnapshot.empty && records3bakSnapshot.empty) {
          // If no records in Firestore, create initial empty records
          await createInitialRecords();
        } else {
          // Process 1BAK records
          if (!records1bakSnapshot.empty) {
            const records1bakList = records1bakSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            
            // Sort by position
            records1bakList.sort((a, b) => a.position - b.position);
            setRecords1bak(records1bakList);
          } else {
            await createInitialRecords('1bak');
          }
          
          // Process 3BAK records
          if (!records3bakSnapshot.empty) {
            const records3bakList = records3bakSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            
            // Sort by position
            records3bakList.sort((a, b) => a.position - b.position);
            setRecords3bak(records3bakList);
          } else {
            await createInitialRecords('3bak');
          }
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching bak records:', err);
        setError('Failed to load bak records. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchBakRecords();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Start editing a record
  const handleEdit = (record, type) => {
    setEditingId(record.id);
    setEditingType(type);
    setFormData({
      position: record.position,
      time: record.time,
      name: record.name
    });
  };

  // Cancel editing
  const handleCancel = () => {
    setEditingId(null);
    setEditingType(null);
    setFormData({
      position: '',
      time: '',
      name: ''
    });
  };

  // Save an edited record
  const handleSave = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      // Validate form data
      if (!formData.time || !formData.name) {
        setError('Time and name are required');
        setLoading(false);
        return;
      }
      
      const recordData = {
        position: parseInt(formData.position),
        time: formData.time,
        name: formData.name
      };
      
      const collectionName = editingType === '1bak' ? 'records1bak' : 'records3bak';
      
      // Update existing record
      await setDoc(doc(db, collectionName, editingId), recordData, { merge: true });
      setMessage(`${editingType.toUpperCase()} record updated successfully`);
      
      // Update local state
      if (editingType === '1bak') {
        setRecords1bak(prevRecords => 
          prevRecords.map(record => 
            record.id === editingId ? { ...record, ...recordData } : record
          ).sort((a, b) => a.position - b.position)
        );
      } else {
        setRecords3bak(prevRecords => 
          prevRecords.map(record => 
            record.id === editingId ? { ...record, ...recordData } : record
          ).sort((a, b) => a.position - b.position)
        );
      }
      
      // Reset form
      handleCancel();
    } catch (err) {
      console.error('Error saving record:', err);
      setError('Failed to save record. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Add a new record
  const handleAddRecord = async (type) => {
    try {
      setLoading(true);
      
      const records = type === '1bak' ? records1bak : records3bak;
      
      // Find the highest position number
      const highestPosition = records.length > 0 
        ? Math.max(...records.map(record => record.position))
        : 0;
      
      // Create a new record with the next position number
      const newRecord = {
        position: highestPosition + 1,
        time: '--:--',
        name: 'T.b.d.'
      };
      
      const collectionName = type === '1bak' ? 'records1bak' : 'records3bak';
      
      // Add to Firestore
      const docRef = await addDoc(collection(db, collectionName), newRecord);
      
      // Update local state
      if (type === '1bak') {
        setRecords1bak(prevRecords => [
          ...prevRecords,
          { id: docRef.id, ...newRecord }
        ].sort((a, b) => a.position - b.position));
      } else {
        setRecords3bak(prevRecords => [
          ...prevRecords,
          { id: docRef.id, ...newRecord }
        ].sort((a, b) => a.position - b.position));
      }
      
      setMessage(`New ${type.toUpperCase()} record added successfully`);
      setError(null);
    } catch (err) {
      console.error('Error adding new record:', err);
      setError('Failed to add new record. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Delete a record
  const handleDeleteRecord = async (recordId, type) => {
    if (!window.confirm(`Are you sure you want to delete this ${type.toUpperCase()} record?`)) {
      return;
    }
    
    try {
      setLoading(true);
      
      const collectionName = type === '1bak' ? 'records1bak' : 'records3bak';
      
      // Delete from Firestore
      await deleteDoc(doc(db, collectionName, recordId));
      
      // Update local state
      if (type === '1bak') {
        setRecords1bak(prevRecords => 
          prevRecords.filter(record => record.id !== recordId)
        );
      } else {
        setRecords3bak(prevRecords => 
          prevRecords.filter(record => record.id !== recordId)
        );
      }
      
      setMessage(`${type.toUpperCase()} record deleted successfully`);
      setError(null);
    } catch (err) {
      console.error('Error deleting record:', err);
      setError('Failed to delete record. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Create initial records if none exist
  const createInitialRecords = async (type = null) => {
    try {
      setLoading(true);
      
      // If type is null, create both types of records
      const types = type ? [type] : ['1bak', '3bak'];
      
      for (const recordType of types) {
        // Create empty records for positions 1-10
        const initialData = Array.from({ length: 10 }, (_, i) => ({
          position: i + 1,
          time: '--:--',
          name: 'T.b.d.'
        }));
        
        const collectionName = recordType === '1bak' ? 'records1bak' : 'records3bak';
        
        // Add initial records
        const addPromises = initialData.map(data => {
          const newDocRef = doc(collection(db, collectionName));
          return setDoc(newDocRef, data).then(() => ({
            id: newDocRef.id,
            ...data
          }));
        });
        
        const newRecords = await Promise.all(addPromises);
        
        if (recordType === '1bak') {
          setRecords1bak(newRecords);
        } else {
          setRecords3bak(newRecords);
        }
      }
    } catch (err) {
      console.error('Error creating initial records:', err);
      setError('Failed to create initial records. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-hsbakbord-section">
      <h2>Manage HSBakbord Records</h2>
      
      {error && <div className="admin-page-error">{error}</div>}
      {message && <div className="admin-page-success">{message}</div>}
      
      {/* Form for editing records */}
      {editingId !== null && (
        <form className="admin-hsbakbord-form" onSubmit={handleSave}>
          <h3>Edit {editingType === '1bak' ? '1BAK' : '3BAK'} Record</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="position">Position</label>
              <input
                type="number"
                id="position"
                name="position"
                value={formData.position}
                onChange={handleInputChange}
                min="1"
                disabled
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="time">Time</label>
              <input
                type="text"
                id="time"
                name="time"
                value={formData.time}
                onChange={handleInputChange}
                placeholder="00:00"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="name">Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>
          
          <div className="form-actions">
            <button type="submit" className="admin-save-btn" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
            <button type="button" className="admin-cancel-btn" onClick={handleCancel}>
              Cancel
            </button>
          </div>
        </form>
      )}
      
      <div className="admin-tables-container">
        {/* 1BAK Records Table */}
        <div className="admin-table-section">
          <div className="admin-table-header">
            <h3>1BAK Records</h3>
            <button 
              className="admin-add-btn" 
              onClick={() => handleAddRecord('1bak')}
              disabled={loading}
            >
              Add 1BAK Record
            </button>
          </div>
          
          <div className="admin-hsbakbord-table-container">
            {loading && !records1bak.length ? (
              <p className="admin-loading">Loading records...</p>
            ) : records1bak.length === 0 ? (
              <p className="admin-no-records">No 1BAK records found.</p>
            ) : (
              <table className="admin-hsbakbord-table">
                <thead>
                  <tr>
                    <th>Position</th>
                    <th>Time</th>
                    <th>Name</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {records1bak.map(record => (
                    <tr key={record.id}>
                      <td>{record.position}</td>
                      <td>{record.time}</td>
                      <td>{record.name}</td>
                      <td className="record-actions">
                        <button 
                          className="admin-edit-btn"
                          onClick={() => handleEdit(record, '1bak')}
                        >
                          Edit
                        </button>
                        <button 
                          className="admin-delete-btn"
                          onClick={() => handleDeleteRecord(record.id, '1bak')}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
        
        {/* 3BAK Records Table */}
        <div className="admin-table-section">
          <div className="admin-table-header">
            <h3>3BAK Records</h3>
            <button 
              className="admin-add-btn" 
              onClick={() => handleAddRecord('3bak')}
              disabled={loading}
            >
              Add 3BAK Record
            </button>
          </div>
          
          <div className="admin-hsbakbord-table-container">
            {loading && !records3bak.length ? (
              <p className="admin-loading">Loading records...</p>
            ) : records3bak.length === 0 ? (
              <p className="admin-no-records">No 3BAK records found.</p>
            ) : (
              <table className="admin-hsbakbord-table">
                <thead>
                  <tr>
                    <th>Position</th>
                    <th>Time</th>
                    <th>Name</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {records3bak.map(record => (
                    <tr key={record.id}>
                      <td>{record.position}</td>
                      <td>{record.time}</td>
                      <td>{record.name}</td>
                      <td className="record-actions">
                        <button 
                          className="admin-edit-btn"
                          onClick={() => handleEdit(record, '3bak')}
                        >
                          Edit
                        </button>
                        <button 
                          className="admin-delete-btn"
                          onClick={() => handleDeleteRecord(record.id, '3bak')}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminHSBakbord;